/**
 * Données fictives des aperçus de panneaux.
 *
 * Portage des méthodes `show*Preview` de `LiveHudController.kt`. Les valeurs
 * sont **reprises telles quelles** : ce sont elles qui rendent les captures et
 * les réglages comparables d'une plateforme à l'autre, et un chiffre inventé
 * ici ferait diverger deux applications qu'on veut pouvoir confronter.
 *
 * Séparé du contrôleur parce qu'il n'y a là aucun DOM : ces fonctions se
 * vérifient sans carte ni navigateur.
 */

import type { RoadbookEntry } from '../core/track'
import { createCustomMarker, type CustomMarker } from '../core/types'

/** Longueur de la trace fictive, en kilomètres. */
export const PREVIEW_TOTAL_KM = 178.5

/** Position fictive des aperçus individuels, en kilomètres. */
export const PREVIEW_POSITION_KM = 12.5

/**
 * Nombre de points de la courbe fictive.
 *
 * Deux cents pour un aperçu seul, cent pour la démo — c'est l'écart d'Android,
 * et il n'est pas cosmétique : à cent points sur 178 km, les points sont assez
 * espacés pour que le fenêtrage du profil se voie. Une courbe dense masquerait
 * un défaut de découpe que cette densité-là révèle.
 */
export const PREVIEW_PROFILE_POINTS = 200
export const DEMO_PROFILE_POINTS = 100

/** Les trois marqueurs fictifs, partagés par le roadbook et le radar. */
export function previewMarkers(): CustomMarker[] {
  return [
    createCustomMarker({ type: 'GPM', name: 'GPM 1', symbol: 'G', distanceKm: 12.8 }),
    createCustomMarker({
      type: 'Ravitaillement',
      name: 'Ravit.',
      symbol: 'R',
      distanceKm: 13.5,
    }),
    createCustomMarker({
      type: 'Sprint',
      name: 'Sprint',
      symbol: 'S',
      distanceKm: 22.8,
    }),
  ]
}

/** Valeurs de l'indicateur kilométrique : position, restant, total. */
export function kmIndicatorPreview(positionKm = PREVIEW_POSITION_KM): {
  currentKm: number
  remainingKm: number
  totalKm: number
} {
  return {
    currentKm: positionKm,
    remainingKm: PREVIEW_TOTAL_KM - positionKm,
    totalKm: PREVIEW_TOTAL_KM,
  }
}

/**
 * Entrées du roadbook de l'aperçu individuel.
 *
 * Les distances restantes — 0,8, 1,5 et 10,8 km — sont **écrites en dur dans
 * Android**, et ne découlent pas de la position 12,5 km des autres aperçus :
 * elles correspondraient à 12,0 km. L'incohérence est reprise plutôt que
 * corrigée, le code d'Android faisant foi ; la démo, elle, calcule.
 */
export function roadbookPreviewEntries(): RoadbookEntry[] {
  const [gpm, feed, sprint] = previewMarkers() as [
    CustomMarker,
    CustomMarker,
    CustomMarker,
  ]
  return [
    { marker: gpm, remainingKm: 0.8 },
    { marker: feed, remainingKm: 1.5 },
    { marker: sprint, remainingKm: 10.8 },
  ]
}

/** Entrées du radar d'alerte : distances calculées depuis la position. */
export function alertPreviewEntries(
  positionKm = PREVIEW_POSITION_KM,
): RoadbookEntry[] {
  return previewMarkers()
    .map((marker) => ({
      marker,
      remainingKm: marker.distanceKm - positionKm,
    }))
    .sort((a, b) => a.remainingKm - b.remainingKm)
}

/**
 * Entrées du roadbook de la démo.
 *
 * Contrairement à l'aperçu individuel, elles découlent de la position : à
 * 12,5 km les trois marqueurs sont devant, et leurs distances restantes sont
 * cohérentes avec celles du radar affiché en même temps — ce que les valeurs
 * en dur du roadbook seul ne seraient pas.
 */
export function demoRoadbookEntries(
  positionKm: number,
  disappearKm: number,
  maxEntries: number,
): RoadbookEntry[] {
  return previewMarkers()
    .map((marker) => ({
      marker,
      remainingKm: marker.distanceKm - positionKm,
    }))
    .filter((entry) => entry.remainingKm > -disappearKm)
    .sort((a, b) => a.remainingKm - b.remainingKm)
    .slice(0, Math.max(maxEntries, 0))
}

/**
 * Courbe d'altitude fictive : deux ondulations complètes sur toute la trace.
 *
 * `400 + 200·sin(4π·i/(n−1))` donne des altitudes de 200 à 600 m, soit des
 * pentes crédibles à cette échelle — assez marquées pour juger la hauteur du
 * profil, sans le mur qu'une amplitude plus forte produirait.
 */
export function profilePreviewSeries(pointCount: number): {
  distances: number[]
  elevations: number[]
} {
  const totalMeters = PREVIEW_TOTAL_KM * 1000
  const last = pointCount - 1

  const distances: number[] = []
  const elevations: number[] = []

  for (let i = 0; i < pointCount; i += 1) {
    distances.push((i * totalMeters) / last)
    elevations.push(400 + 200 * Math.sin((i * 4 * Math.PI) / last))
  }

  return { distances, elevations }
}
